import axios from 'axios';
import dayjs from 'dayjs';
import { stringify } from 'query-string';
import { getEnv } from '../env';

// a tiny piece of the data returned from the AerisWeather API
export interface XWeatherConditionsSummary {
  weather: {
    icon: string;
  };
  temp: {
    avgF: number;
    maxF: number;
    minF: number;
  };
  profile: {
    tz: string;
  };
}

// a tiny piece of the data returned from the AerisWeather API
// https://www.xweather.com/docs/weather-api/endpoints/conditions#parameters
export interface XWeatherConditions {
  icon: string;
  spressureIN: number | null;
  tempF: number | null;
  windDir: string | null;
  windDirDEG: number | null;
  windSpeedMPH: number | null;
  sky: number | null; // cloud cover
  weatherPrimary: string;
  pop: number | null; // probability of precipitation
  profile: {
    tz: string;
  };
}

// a tiny piece of the data returned from the AerisWeather API
// https://www.xweather.com/docs/weather-api/endpoints/sunmoon#parameters
export interface XWeatherSunMoon {
  sun: {
    riseISO: string | null;
    setISO: string | null;
  };
  moon: {
    riseISO: string | null;
    setISO: string | null;
    transitISO: string | null;
    underfootISO: string | null;
    phase: {
      phase: number;
      illum: number;
      name:
        | 'first quarter'
        | 'full moon'
        | 'last quarter'
        | 'new moon'
        | 'waning crescent'
        | 'waning gibbous'
        | 'waxing crescent'
        | 'waxing gibbous';
    };
  };
  profile: {
    tz: string;
  };
}

const env = getEnv();

interface XWeatherConditionsSummaryArgs {
  lat: number;
  lng: number;
  timestamp?: string;
}

export const xWeatherConditionsDailySummary = ({
  lat,
  lng,
  timestamp,
}: XWeatherConditionsSummaryArgs) => {
  const date = dayjs(timestamp).format('YYYY-MM-DD');
  const queryParams: Record<string, string> = {
    client_id: env.AERIS_ID,
    client_secret: env.AERIS_SECRET_KEY,
    from: `${date}T00:00:00`,
    to: `${date}T23:59:59`,
  };

  return axios.get<{ response: { periods: XWeatherConditionsSummary[] }[] }>(
    `https://data.api.xweather.com/conditions/summary/${lat},${lng}?${stringify(queryParams)}`
  );
};

interface XWeatherConditionsFull24HoursArgs {
  lat: number;
  lng: number;
  from: string;
  to: string;
}

export const xWeatherConditionsFromTo = ({
  lat,
  lng,
  from,
  to,
}: XWeatherConditionsFull24HoursArgs) => {
  const queryParams: Record<string, string> = {
    client_id: env.AERIS_ID,
    client_secret: env.AERIS_SECRET_KEY,
    from,
    to,
  };

  return axios.get<{ response: { periods: XWeatherConditions[] }[] }>(
    `https://data.api.xweather.com/conditions/${lat},${lng}?${stringify(queryParams)}`
  );
};

interface XWeatherConditionsCurrentArgs {
  lat: number;
  lng: number;
}

export const xWeatherConditionsCurrent = ({ lat, lng }: XWeatherConditionsCurrentArgs) => {
  const queryParams: Record<string, string> = {
    client_id: env.AERIS_ID,
    client_secret: env.AERIS_SECRET_KEY,
  };

  return axios.get<{ response: { periods: XWeatherConditions[] }[] }>(
    `https://data.api.xweather.com/conditions/${lat},${lng}?${stringify(queryParams)}`
  );
};

interface XWeatherConditionsAtTimestampArgs {
  lat: number;
  lng: number;
  timestamp: string;
}

export const xWeatherConditionsAtTimestamp = ({
  lat,
  lng,
  timestamp,
}: XWeatherConditionsAtTimestampArgs) => {
  const queryParams: Record<string, string> = {
    client_id: env.AERIS_ID,
    client_secret: env.AERIS_SECRET_KEY,
    from: timestamp,
  };

  return axios.get<{ response: { periods: XWeatherConditions[] }[] }>(
    `https://data.api.xweather.com/conditions/${lat},${lng}?${stringify(queryParams)}`
  );
};

interface XWeatherSunMoonArgs {
  lat: number;
  lng: number;
  timestamp: string;
}

export const xWeatherSunMoon = ({ lat, lng, timestamp }: XWeatherSunMoonArgs) => {
  const queryParams: Record<string, string> = {
    client_id: env.AERIS_ID,
    client_secret: env.AERIS_SECRET_KEY,
    from: timestamp,
  };

  return axios.get<{ response: XWeatherSunMoon[] }>(
    `https://data.api.xweather.com/sunmoon/${lat},${lng}?${stringify(queryParams)}`
  );
};
